"use client"
import * as Dialog from '@radix-ui/react-dialog'
import { X, CheckCircle, AlertTriangle } from 'lucide-react'
import Button from '@/components/ui/Button'
import { Badge } from '@radix-ui/themes'
import Link from 'next/link'

interface ServiceMeta {
  _id: string;
  serviceName: string;
  slug: string;
  metaTitle: string;
  metaDescription: string;
  canonicalUrl: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  status: 'Draft' | 'Published';
}

interface ServiceMetaSeoModalProps {
  isOpen: boolean
  onClose: () => void
  serviceMeta: ServiceMeta | null
}

interface SeoCheck {
  name: string;
  status: 'good' | 'warning' | 'error';
  score: number;
  recommendation: string;
  value: string;
}

const getSeoScoreColor = (score: number) => {
  if (score >= 90) return 'bg-green-100 text-green-800';
  if (score >= 80) return 'bg-blue-100 text-blue-800';
  if (score >= 70) return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
};

const getStatusIcon = (status: 'good' | 'warning' | 'error') => {
  switch (status) {
    case 'good': return <CheckCircle className="h-4 w-4 text-green-600" />;
    case 'warning': return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
    case 'error': return <AlertTriangle className="h-4 w-4 text-red-600" />;
  }
};

export default function ServiceMetaSeoModal({ isOpen, onClose, serviceMeta }: ServiceMetaSeoModalProps) {
  if (!serviceMeta) return null;

  // Calculate detailed SEO checks
  const seoChecks: SeoCheck[] = [
    {
      name: 'Meta Title',
      status: serviceMeta.metaTitle?.length >= 30 && serviceMeta.metaTitle?.length <= 60 ? 'good' :
             serviceMeta.metaTitle?.length >= 20 && serviceMeta.metaTitle?.length <= 70 ? 'warning' : 'error',
      score: serviceMeta.metaTitle?.length >= 30 && serviceMeta.metaTitle?.length <= 60 ? 20 :
             serviceMeta.metaTitle?.length >= 20 && serviceMeta.metaTitle?.length <= 70 ? 10 : 0,
      recommendation: 'Meta title should be between 30-60 characters for optimal SEO',
      value: `${serviceMeta.metaTitle?.length || 0} characters`
    },
    {
      name: 'Meta Description',
      status: serviceMeta.metaDescription?.length >= 120 && serviceMeta.metaDescription?.length <= 160 ? 'good' :
             serviceMeta.metaDescription?.length >= 100 && serviceMeta.metaDescription?.length <= 180 ? 'warning' : 'error',
      score: serviceMeta.metaDescription?.length >= 120 && serviceMeta.metaDescription?.length <= 160 ? 20 :
             serviceMeta.metaDescription?.length >= 100 && serviceMeta.metaDescription?.length <= 180 ? 10 : 0,
      recommendation: 'Meta description should be between 120-160 characters for optimal SEO',
      value: `${serviceMeta.metaDescription?.length || 0} characters`
    },
    {
      name: 'Open Graph Tags',
      status: serviceMeta.ogTitle && serviceMeta.ogDescription && serviceMeta.ogImage ? 'good' : 'error',
      score: serviceMeta.ogTitle && serviceMeta.ogDescription && serviceMeta.ogImage ? 10 : 0,
      recommendation: 'Add og:title, og:description, and og:image for better social media sharing',
      value: `${[serviceMeta.ogTitle && 'Title', serviceMeta.ogDescription && 'Description', serviceMeta.ogImage && 'Image'].filter(Boolean).join(', ') || 'None'}`
    },
    {
      name: 'Twitter Cards',
      status: serviceMeta.twitterTitle && serviceMeta.twitterDescription && serviceMeta.twitterImage ? 'good' : 'warning',
      score: serviceMeta.twitterTitle && serviceMeta.twitterDescription && serviceMeta.twitterImage ? 5 : 0,
      recommendation: 'Add Twitter card meta tags for better Twitter sharing (optional but recommended)',
      value: `${[serviceMeta.twitterTitle && 'Title', serviceMeta.twitterDescription && 'Description', serviceMeta.twitterImage && 'Image'].filter(Boolean).join(', ') || 'None'}`
    },
    {
      name: 'Canonical URL',
      status: serviceMeta.canonicalUrl ? 'good' : 'error',
      score: serviceMeta.canonicalUrl ? 5 : 0,
      recommendation: 'Canonical URL helps prevent duplicate content issues',
      value: serviceMeta.canonicalUrl ? 'Present' : 'Missing'
    },
    {
      name: 'Slug Optimization',
      status: serviceMeta.slug?.length > 0 && !serviceMeta.slug?.includes('_') && serviceMeta.slug?.length <= 60 ? 'good' :
             serviceMeta.slug?.length > 0 && serviceMeta.slug?.length <= 80 ? 'warning' : 'error',
      score: serviceMeta.slug?.length > 0 && !serviceMeta.slug?.includes('_') && serviceMeta.slug?.length <= 60 ? 5 :
             serviceMeta.slug?.length > 0 && serviceMeta.slug?.length <= 80 ? 3 : 0,
      recommendation: 'Use hyphens instead of underscores, keep under 60 characters',
      value: `${serviceMeta.slug?.length || 0} characters`
    }
  ];

  const totalScore = seoChecks.reduce((sum, check) => sum + check.score, 50); // Base score of 50
  const finalScore = Math.min(100, totalScore);

  const overallStatus = finalScore >= 90 ? 'Excellent' :
                       finalScore >= 80 ? 'Good' :
                       finalScore >= 70 ? 'Fair' : 'Needs Improvement';

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-xl z-[101]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="seo-modal-title"
          aria-describedby="seo-modal-description"
        >
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <Dialog.Title id="seo-modal-title" className="text-xl font-semibold text-gray-900">
                  SEO Analysis: {serviceMeta.serviceName}
                </Dialog.Title>
                <p id="seo-modal-description" className="text-sm text-gray-600 mt-1">
                  Detailed SEO performance breakdown and recommendations
                </p>
              </div>
              <Dialog.Close asChild>
                <button
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close SEO analysis dialog"
                >
                  <X className="h-5 w-5" />
                </button>
              </Dialog.Close>
            </div>

            {/* Overall Score */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Overall SEO Score</h3>
                <Badge variant="soft" className={getSeoScoreColor(finalScore)}>
                  {finalScore}/100 - {overallStatus}
                </Badge>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div
                  className={`h-3 rounded-full ${
                    finalScore >= 90 ? 'bg-green-600' :
                    finalScore >= 80 ? 'bg-blue-600' :
                    finalScore >= 70 ? 'bg-yellow-600' : 'bg-red-600'
                  }`}
                  style={{ width: `${finalScore}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600">
                Base score: 50 | Additional points from checks: {totalScore - 50}
              </p>
            </div>

            {/* SEO Checks */}
            <div className="space-y-4 mb-6">
              <h3 className="text-lg font-medium text-gray-900">Detailed Analysis</h3>
              {seoChecks.map((check, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(check.status)}
                      <h4 className="font-medium text-gray-900">{check.name}</h4>
                    </div>
                    <span className="text-sm font-medium text-gray-600">+{check.score} points</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{check.recommendation}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Current: {check.value}</span>
                    <Badge
                      variant="soft"
                      color={
                        check.status === 'good' ? 'green' :
                        check.status === 'warning' ? 'yellow' : 'red'
                      }
                    >
                      {check.status === 'good' ? 'Good' :
                       check.status === 'warning' ? 'Warning' : 'Error'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>

            {/* Meta Information */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Meta Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meta Title</label>
                  <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded border">
                    {serviceMeta.metaTitle || 'Not set'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                  <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded border">
                    {serviceMeta.metaDescription || 'Not set'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Canonical URL</label>
                  <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded border">
                    {serviceMeta.canonicalUrl || 'Not set'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                  <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded border">
                    /{serviceMeta.slug || 'Not set'}
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                aria-label="Close SEO analysis"
              >
                Close
              </Button>
              <Link href={`/admin/service-meta/${serviceMeta._id}/edit`}>
                <Button
                  type="button"
                  variant="primary"
                >
                  Edit Meta Data
                </Button>
              </Link>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
